/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {ReactNode, useEffect, useState} from 'react';
import isEmpty from 'lodash.isempty';
import isString from 'lodash.isstring';
import {flattenAccountTree} from '@ion/core/src/entities/Accounts';
import EntityLabel from '@ion/core/src/entities/EntityLabel';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {currentUser, getSubAccounts} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import GroupProperties from '@ion/core/src/services/GroupProperties';
import {Account, AccountLink, EntityStatus, Link} from '@ion/core/src/types/entities';
import Dropdown, {createOptions, Options} from '../components/menus/Dropdown';

export type GroupChooserProps = {
  readOnly?: boolean;
  onLoad?: (arg: boolean) => void;
  onChange?: (account: Account) => void;
  defaultValue?: Account;
  account?: Account;
  movedToAccount?: Account;
  parentAccount?: Account;
  accounts?: Link[] | Account[];
  defaultAccount?: AccountLink[];
  fieldLabel?: string;
  required?: boolean;
  hasError?: boolean;
  errorText?: string;
  placeholder?: string | ReactNode;
  name?: string;
  setAccounts?: (accounts: Account[]) => void;
  useDisabledStyles?: boolean;
  value?: Account;
  includeGroupProperties?: boolean;
  isRoleViewMode?: boolean;
  notificationGroupChooser?: boolean;
  notificationFieldLabel?: string;
  primaryButtonDisabled?: boolean;
  hasDeviceMoveOption?: boolean;
  includeSearchIcon?: boolean;
  showAccountId?: boolean;
};

export interface DisableAccountChooser {
  disableAccountChooser?: boolean;
}

/**
 * Generates the group chooser dropdown labels and values based on
 * the provided account tree
 */
export const getGroupOptions = (
  accountTree: Account[],
  showAccountId = false
): Options[] => {
  return createOptions({
    items     : accountTree,
    augmentor : (item: Account, option: {value: Account; label: Account;}) => {
      const baseValue      = EntityLabel.get(option.value);
      const baseLabel      = EntityLabel.get(option.label);
      const formattedLabel = `${baseLabel} ${
        showAccountId ? `(${entityId(option.label)})` : ''
      }`.trim();

      return {
        value    : baseValue,
        label    : formattedLabel,
        disabled : item?.status !== EntityStatus.Enabled
      };
    }
  });
};

/**
 * Handles fetching the user's account and subAccounts
 * so they can be populated to the chooser as options.
 */
export const getGroups = async(account) => {
  try {
    const user = account || (await currentUser());
    const userAccount = user?.account || user;
    const subAccounts = await getSubAccounts(entityId(userAccount.href), true);
    const allChildren = flattenAccountTree(subAccounts);
    return [userAccount, ...allChildren];
  } catch (err) {
    error(err);
  }
};

/**
 * Sorts the flattened account tree before populating the chooser.
 * The user's account is retained as the first item in the list; the
 * remainder of the list is sorted alphabetically by account title.
 */
const sortAccountTree = (accountTree: Account[]): Account[] => {
  const [first, ...rest] = accountTree;
  return [first]?.concat(rest?.sort((a, b) => a?.title > b?.title ? 1 : -1));
};

/**
 * @GroupChooser
 *
 * The GroupChooser provides a list of group options based on up
 * to 2 account lists (that may include sub accounts) that are merged
 * together and flattened for consumption by the dropdown component.
 * The selection changes and loading state can be subscribed to.
 *
 * ```typescript
 *
 *     import {GroupChooser} from '@ion/dls/src/editors/GroupChooser';
 *
 *    <GroupChooser required
 *      account={account}
 *      subAccounts={subAccounts} />
 *
 *    // read-only mode ...
 *    <GroupChooser readOnly />
 *
 *    // no default value with basic callback subscriptions ...
 *    <GroupChooser required
 *      account={account}
 *      onLoad={checkIsLoading}
 *      onChange={handleGroupChange}
 *      name='assetGroup' />
 *
 *    // provide a default value and error handling ...
 *    <GroupChooser required
 *      hasError={groupError}
 *      accounts={accounts}
 *      onLoad={checkIsLoading}
 *      onChange={handleGroupChange}
 *      defaultValue={{
 *        href   : 'https://qa.connect.calamp.com/connect/services/accounts/36131',
 *        rel    : 'subAccount',
 *        title  : 'Engineering',
 *        status : 'Enabled'
 *      }} />
 *
 * ```
 *
 * @param {Function} onLoad: Callback that passes a boolean value to the provided
 *   function that determines the loading status of the inherited values.
 * @param {Function} onChange: Callback prop which will be invoked with Group Properties
 *   inherited properties.
 * @param {object} defaultValue: account object that can be set as the default value.
 *   If provided the getInheritedProperties is immediately invoked and properties are passed
 *   to the onChange function.
 * @param {object} account: optional account object that is used to fetch subAccounts
 * @param {Array} accounts: list of accounts (that may include subAccounts) that is used
 *   as the list to build the chooser options. Automatically flattened.
 * @param {string} fieldLabel: Overrides the default field label
 * @param {boolean} required: Determines if the dropdown should be marked as required and
 *   teh asterisk shown on the fieldLabel. Defaults to false.
 * @param {boolean} hasError: Passthrough prop sent to the Dropdown that will show a red box
 *   around the dropdown if true. Defaults to false.
 * @param {boolean} errorText: Passthrough prop sent to the Dropdown that contains the text
 *   (red) to display if hasError is true. Defaults to ''.
 * @param {boolean} readOnly: When true, the component will be displayed in read-only mode;
 *   that is, disabled. Defaults to false.
 * @param {string | Node} placeholder: This overrides the default placeholder for the dropdown
 * @param {string} name: for the component that gets passed to the root element.
 */
export const GroupChooser = ({
  onLoad,
  onChange,
  defaultValue,
  account,
  accounts,
  placeholder,
  name,
  setAccounts,
  value,
  isRoleViewMode,
  parentAccount,
  primaryButtonDisabled,
  notificationFieldLabel,
  movedToAccount = {},
  fieldLabel = translate('common:editors.shared.groupField'),
  required = false,
  hasError = false,
  errorText = '',
  readOnly = false,
  hasDeviceMoveOption = false,
  useDisabledStyles = false,
  includeGroupProperties = true,
  notificationGroupChooser = false,
  includeSearchIcon = false,
  showAccountId = false
}: GroupChooserProps) => {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [groupOptions, setGroupOptions]   = useState([]);
  const [accountTree, setAccountTree]     = useState([]);
  const [isLoading, setIsLoading]         = useState(false);
  const hasMounted                        = useHasMounted();

  useEffect(() => {
    getAccounts();
    async function getAccounts() {
      handleIsLoading(true);
      const accountList = accounts || (await getGroups(account));
      const accountTree = sortAccountTree(accountList);
      const selectedAccount = hasDeviceMoveOption && !isEmpty(movedToAccount)
        ? EntityLabel.get(movedToAccount) === EntityLabel.get(defaultValue)
          ? defaultValue : movedToAccount : defaultValue;
      const hasParent = accountTree?.findIndex(account =>
        entityId(account?.href) === entityId(parentAccount?.href)) > -1;
      if (parentAccount && !hasParent) {
        accountTree?.unshift(parentAccount);
      }
      setAccountTree(accountTree);
      setAccounts?.(accountTree);
      const createdGroupOptions = getGroupOptions(accountTree, showAccountId);
      
      setGroupOptions(createdGroupOptions);
      if (selectedAccount) {
        const matchingAccount = isString(selectedAccount)
          ? accountTree.find((defaultValue) => EntityLabel.get(defaultValue).toLowerCase() ===
          selectedAccount.toLowerCase()) : selectedAccount;
        return await setSelectionChange(
          matchingAccount?.title,
          entityId(matchingAccount?.href),
          matchingAccount
        );
      }
      handleIsLoading(false);
    }
  }, [defaultValue]);

  // If the user needs to pass tha group value to the chooser. We only update
  // the dropdown value if the user passes something different than what is
  // selected to preserve the encapsulated nature of the chooser.
  useEffect(() => {
    const label = EntityLabel.get(value);
    if (label && (label !== selectedGroup)) {
      handleChange({target: {value: label}});
    }
  }, [value]);

  function handleIsLoading(value) {
    onLoad?.(value);
    setIsLoading(value);
  };

  function handleChange({target}) {
    const account = accountTree.find(account => (
      `${EntityLabel.get(account)} ${showAccountId ? `(${entityId(account)})` : ''}`.trim() === target.value)
    );
    const groupId = entityId(account);
    setSelectionChange(target.value, groupId, account);
  }

  async function setSelectionChange(value, id, account) {
    setSelectedGroup(translate('common:states.loading'));
    handleIsLoading(true);
    const selectedAccountName = `${EntityLabel.get(account)} ${showAccountId ? `(${entityId(account)})` : ''}`.trim();
    setSelectedGroup(selectedAccountName);
    if (hasMounted !== false && includeGroupProperties && !includeSearchIcon) {
      await GroupProperties.getInheritedProperties(id);
    }
    onChange?.(account);
    handleIsLoading(false);
  }

  return (
    <Dropdown displaySelectedLabel
      required={required}
      hasError={hasError}
      errorText={errorText}
      placeholder={placeholder || translate('common:editors.shared.selectGroup')}
      disabled={readOnly || isLoading || isRoleViewMode || primaryButtonDisabled}
      loading={isLoading}
      useDisabledStyles={useDisabledStyles}
      options={groupOptions}
      fieldLabel={notificationGroupChooser? notificationFieldLabel : fieldLabel}
      onChange={handleChange}
      value={selectedGroup}
      includeSearchIcon={includeSearchIcon}
      name={name} />
  );
};

export default GroupChooser;
